/**
 * Zod schemas mirroring the wire types in `rest.ts` and `ws.ts`, plus parse
 * helpers. These are the runtime validators every track uses at trust
 * boundaries (Worker request parsing, WS frame routing, client response checks).
 *
 * Source of truth: `docs/prd/07-api-contract.md`. The `z.infer<>` of each schema
 * is assignable to its hand-written counterpart in `rest.ts` / `ws.ts`.
 */

import { z } from 'zod';
import { REST_ERROR_CODES, WS_ERROR_CODES } from './errors';
import { WS_CONTENT_TYPES, WS_FRAME_TYPES, WS_PROTOCOL_VERSION } from './ws';

/* -------------------------------------------------------------------------- */
/*  Primitive scalars                                                          */
/* -------------------------------------------------------------------------- */

/** UUID (v4/v7). */
export const zUuid = z.string().uuid();
/** ULID — 26-char Crockford base32. */
export const zUlid = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'invalid ULID');
/** base64url string (no padding). */
export const zBase64Url = z.string().regex(/^[A-Za-z0-9_-]+$/, 'invalid base64url');
/** Epoch milliseconds. */
export const zEpochMs = z.number().int().nonnegative();
/** Opaque pagination cursor; `null` ⇒ no more pages. */
export const zCursor = z.string().nullable();

export const zContentType = z.enum(WS_CONTENT_TYPES);
export const zConversationType = z.enum(['direct', 'group']);
export const zMemberRole = z.enum(['member', 'admin']);
export const zDevicePlatform = z.enum(['web', 'web-desktop', 'web-mobile-pwa']);
export const zLastSeenVisibility = z.enum(['everyone', 'contacts', 'nobody']);
export const zOnlineVisibility = z.enum(['everyone', 'contacts', 'nobody']);
export const zPublicPresence = z.enum(['online', 'offline', 'unknown']);
export const zPresenceState = z.enum(['online', 'offline']);
export const zMemberChangeAction = z.enum(['added', 'removed', 'role', 'left']);

/* -------------------------------------------------------------------------- */
/*  REST — Devices (§3.2)                                                      */
/* -------------------------------------------------------------------------- */

export const zRegisterDeviceRequest = z.object({
  device_id: zUlid,
  name: z.string().max(120),
  platform: zDevicePlatform,
  push_capable: z.boolean(),
});

export const zRegisterDeviceResponse = z.object({
  device_id: zUlid,
  user_id: zUuid,
  registered_at_ms: zEpochMs,
  is_new: z.boolean(),
});

/* -------------------------------------------------------------------------- */
/*  REST — Profile (§3.3)                                                      */
/* -------------------------------------------------------------------------- */

export const zMeSettings = z.object({
  last_seen_visibility: zLastSeenVisibility,
  read_receipts_enabled: z.boolean(),
  online_visibility: zOnlineVisibility,
});

export const zMeResponse = z.object({
  user_id: zUuid,
  handle: z.string(),
  display_name: z.string(),
  about: z.string(),
  avatar_url: z.string().url().nullable(),
  settings: zMeSettings,
  registered_at_ms: zEpochMs,
});

export const zUpdateMeRequest = z
  .object({
    display_name: z.string().min(1).max(80),
    about: z.string().max(300),
    avatar_url: z.string().url().nullable(),
    settings: zMeSettings.partial(),
  })
  .partial();

export const zPublicUserResponse = z.object({
  user_id: zUuid,
  handle: z.string(),
  display_name: z.string(),
  avatar_url: z.string().url().nullable(),
  last_seen_at_ms: zEpochMs.optional(),
  presence: zPublicPresence,
});

/* -------------------------------------------------------------------------- */
/*  REST — Contacts (§3.4)                                                     */
/* -------------------------------------------------------------------------- */

export const zContact = z.object({
  user_id: zUuid,
  handle: z.string(),
  display_name: z.string(),
  avatar_url: z.string().url().nullable(),
});

export const zListContactsResponse = z.object({
  contacts: z.array(zContact),
  cursor: zCursor,
});

export const zAddContactRequest = z.union([
  z.object({ handle: z.string().min(1) }),
  z.object({ phone_e164: z.string().regex(/^\+[1-9]\d{6,14}$/, 'invalid E.164 phone') }),
]);

export const zAddContactResponse = z.object({
  user_id: zUuid,
  handle: z.string(),
  display_name: z.string(),
});

/* -------------------------------------------------------------------------- */
/*  REST — Prekeys (§3.5)                                                      */
/* -------------------------------------------------------------------------- */

export const zSignedPrekey = z.object({
  key_id: z.number().int().nonnegative(),
  public_key: zBase64Url,
  signature: zBase64Url,
});

export const zOneTimePrekey = z.object({
  key_id: z.number().int().nonnegative(),
  public_key: zBase64Url,
});

export const zPublishKeysRequest = z.object({
  device_id: zUlid,
  registration_id: z.number().int().nonnegative(),
  identity_key: zBase64Url,
  signed_prekey: zSignedPrekey,
  one_time_prekeys: z.array(zOneTimePrekey).max(1000),
});

export const zPublishKeysResponse = z.object({
  published: z.literal(true),
  one_time_prekey_count: z.number().int().nonnegative(),
});

export const zTopUpOneTimeKeysRequest = z.object({
  device_id: zUlid,
  one_time_prekeys: z.array(zOneTimePrekey).min(1).max(1000),
});

export const zTopUpOneTimeKeysResponse = z.object({
  one_time_prekey_count: z.number().int().nonnegative(),
});

export const zDeviceKeyBundle = z.object({
  device_id: zUlid,
  registration_id: z.number().int().nonnegative(),
  identity_key: zBase64Url,
  signed_prekey: zSignedPrekey,
  one_time_prekey: zOneTimePrekey.nullable(),
});

export const zGetKeysResponse = z.object({
  user_id: zUuid,
  devices: z.array(zDeviceKeyBundle),
});

export const zGetKeysQuery = z.object({
  device_id: zUlid.optional(),
});

export const zKeyCountResponse = z.object({
  one_time_prekey_count: z.number().int().nonnegative(),
});

/* -------------------------------------------------------------------------- */
/*  REST — Conversations (§3.6)                                                */
/* -------------------------------------------------------------------------- */

export const zLastMessagePreview = z.object({
  seq: z.number().int().nonnegative(),
  sender_id: zUuid,
  content_type: zContentType,
  server_ts: zEpochMs,
  has_media: z.boolean(),
});

export const zConversationListItem = z.object({
  id: zUuid,
  type: zConversationType,
  title: z.string().nullable(),
  avatar_url: z.string().url().nullable(),
  last_message: zLastMessagePreview.nullable(),
  unread_count: z.number().int().nonnegative(),
  my_role: zMemberRole,
  muted_until_ms: zEpochMs.nullable(),
  updated_at_ms: zEpochMs,
});

export const zListConversationsResponse = z.object({
  conversations: z.array(zConversationListItem),
  cursor: zCursor,
});

export const zListConversationsQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).optional(),
  cursor: z.string().optional(),
});

export const zCreateDirectConversationRequest = z.object({
  type: z.literal('direct'),
  peer_user_id: zUuid,
  client_msg_id: zUlid,
});

export const zCreateGroupConversationRequest = z.object({
  type: z.literal('group'),
  title: z.string().min(1).max(120),
  member_user_ids: z.array(zUuid).max(255),
  avatar_url: z.string().url().nullable().optional(),
  client_msg_id: zUlid,
});

export const zCreateConversationRequest = z.discriminatedUnion('type', [
  zCreateDirectConversationRequest,
  zCreateGroupConversationRequest,
]);

export const zConversationMemberSummary = z.object({
  user_id: zUuid,
  role: zMemberRole,
});

export const zCreateConversationResponse = z.object({
  id: zUuid,
  type: zConversationType,
  title: z.string().nullable(),
  members: z.array(zConversationMemberSummary),
  created_at_ms: zEpochMs,
});

export const zConversationMember = z.object({
  user_id: zUuid,
  role: zMemberRole,
  joined_at_ms: zEpochMs,
  display_name: z.string(),
});

export const zConversationDetailResponse = z.object({
  id: zUuid,
  type: zConversationType,
  title: z.string().nullable(),
  avatar_url: z.string().url().nullable(),
  my_role: zMemberRole,
  muted_until_ms: zEpochMs.nullable(),
  members: z.array(zConversationMember),
  created_at_ms: zEpochMs,
  updated_at_ms: zEpochMs,
});

export const zUpdateConversationRequest = z
  .object({
    title: z.string().min(1).max(120),
    avatar_url: z.string().url().nullable(),
  })
  .partial();

/* -------------------------------------------------------------------------- */
/*  REST — Member management (§3.7)                                            */
/* -------------------------------------------------------------------------- */

export const zListMembersResponse = z.object({
  members: z.array(zConversationMember),
});

export const zAddMembersRequest = z.object({
  user_ids: z.array(zUuid).min(1).max(255),
  client_msg_id: zUlid,
});

export const zSkippedMember = z.object({
  user_id: zUuid,
  reason: z.string(),
});

export const zAddMembersResponse = z.object({
  added: z.array(zUuid),
  skipped: z.array(zSkippedMember),
  member_count: z.number().int().nonnegative(),
});

export const zSetMemberRoleRequest = z.object({ role: zMemberRole });

export const zSetMemberRoleResponse = z.object({
  user_id: zUuid,
  role: zMemberRole,
});

/* -------------------------------------------------------------------------- */
/*  REST — Message history (§3.8)                                              */
/* -------------------------------------------------------------------------- */

export const zGetMessagesQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).optional(),
  before_seq: z.coerce.number().int().nonnegative().optional(),
  after_seq: z.coerce.number().int().nonnegative().optional(),
  cursor: z.string().optional(),
});

export const zMessageMediaRef = z.object({
  media_id: zUuid,
  key: zBase64Url,
  mime: z.string(),
  size: z.number().int().nonnegative(),
  enc_meta_in_body: z.literal(true),
});

export const zHistoryMessage = z.object({
  seq: z.number().int().nonnegative(),
  message_id: zUuid,
  sender_id: zUuid,
  client_msg_id: zUlid,
  content_type: zContentType,
  ciphertext: zBase64Url.nullable(),
  ratchet_header: zBase64Url.nullable(),
  media: zMessageMediaRef.nullable(),
  reply_to_seq: z.number().int().nonnegative().nullable(),
  server_ts: zEpochMs,
  edited: z.boolean(),
  deleted: z.boolean(),
});

export const zMessagesPage = z.object({
  has_more: z.boolean(),
  next_cursor: zCursor,
  oldest_seq: z.number().int().nonnegative().nullable(),
  newest_seq: z.number().int().nonnegative().nullable(),
});

export const zGetMessagesResponse = z.object({
  conversation_id: zUuid,
  messages: z.array(zHistoryMessage),
  page: zMessagesPage,
});

/* -------------------------------------------------------------------------- */
/*  REST — Media (§3.9)                                                        */
/* -------------------------------------------------------------------------- */

export const zPresignUploadRequest = z.object({
  conversation_id: zUuid,
  content_length: z.number().int().positive(),
  content_type: z.string(),
  client_msg_id: zUlid,
});

export const zPresignUploadResponse = z.object({
  media_id: zUuid,
  upload_url: z.string().url(),
  key: z.string(),
  max_bytes: z.number().int().positive(),
  expires_at_ms: zEpochMs,
});

export const zPresignDownloadRequest = z.object({
  conversation_id: zUuid,
  media_id: zUuid,
});

export const zPresignDownloadResponse = z.object({
  download_url: z.string().url(),
  expires_at_ms: zEpochMs,
});

/* -------------------------------------------------------------------------- */
/*  REST — Push (§3.10)                                                        */
/* -------------------------------------------------------------------------- */

export const zPushSubscriptionKeys = z.object({
  p256dh: zBase64Url,
  auth: zBase64Url,
});

export const zRegisterPushRequest = z.object({
  device_id: zUlid,
  endpoint: z.string().url(),
  keys: zPushSubscriptionKeys,
});

export const zRegisterPushResponse = z.object({
  subscription_id: zUuid,
  registered_at_ms: zEpochMs,
});

export const zUnregisterPushRequest = z.object({
  endpoint: z.string().url(),
});

/* -------------------------------------------------------------------------- */
/*  REST — WS ticket (§3.11)                                                   */
/* -------------------------------------------------------------------------- */

export const zRealtimeTicketRequest = z.object({ device_id: zUlid });

export const zRealtimeTicketResponse = z.object({
  ticket: z.string(),
  expires_at_ms: zEpochMs,
  ttl_seconds: z.number().int().positive(),
});

export const zHealthResponse = z.object({ ok: z.literal(true), ts: zEpochMs });

/* -------------------------------------------------------------------------- */
/*  REST — Error envelope (§8.1)                                               */
/* -------------------------------------------------------------------------- */

export const zRestErrorCode = z.enum(
  Object.keys(REST_ERROR_CODES) as [string, ...string[]],
);

export const zFieldError = z.object({
  field: z.string(),
  message: z.string(),
});

export const zApiErrorBody = z.object({
  code: zRestErrorCode,
  message: z.string(),
  http_status: z.number().int(),
  request_id: zUlid,
  details: z.union([z.array(zFieldError), z.record(z.unknown()), z.null()]),
});

export const zApiErrorResponse = z.object({ error: zApiErrorBody });

/* -------------------------------------------------------------------------- */
/*  WebSocket — common envelope + sub-shapes (§5.1)                            */
/* -------------------------------------------------------------------------- */

export const zWsFrameType = z.enum(WS_FRAME_TYPES);
export const zWsErrorCode = z.enum(Object.keys(WS_ERROR_CODES) as [string, ...string[]]);

/** Common envelope fields shared by every frame. */
export const zWsEnvelopeBase = z.object({
  v: z.literal(WS_PROTOCOL_VERSION),
  id: zUlid,
  ts: zEpochMs,
  cid: zUuid.optional(),
});

export const zWsStoredMessage = z.object({
  seq: z.number().int().nonnegative(),
  sender_id: zUuid,
  client_msg_id: zUlid,
  content_type: zContentType,
  ciphertext: zBase64Url,
  ratchet_header: zBase64Url,
  media_id: zUuid.optional(),
  reply_to_seq: z.number().int().nonnegative().optional(),
  server_ts: zEpochMs,
});

export const zWsSyncTarget = z.object({
  cid: zUuid,
  since_seq: z.number().int().nonnegative(),
});

/** Helper: extend the common envelope with a `t` literal + payload fields. */
function frame<T extends z.ZodRawShape>(t: (typeof WS_FRAME_TYPES)[number], shape: T) {
  return zWsEnvelopeBase.extend({ t: z.literal(t), ...shape });
}

/* ---- Client → Server frames (§5.2) ---- */

export const zAuthRefreshFrame = frame('auth.refresh', { ticket: z.string() });

export const zSyncRequestFrame = frame('sync.request', {
  targets: z.array(zWsSyncTarget),
});

export const zMsgSendFrame = frame('msg.send', {
  cid: zUuid,
  client_msg_id: zUlid,
  content_type: zContentType,
  ciphertext: zBase64Url,
  ratchet_header: zBase64Url,
  media_id: zUuid.optional(),
  reply_to_seq: z.number().int().nonnegative().optional(),
});

export const zMsgDeliveredClientFrame = frame('msg.delivered', {
  cid: zUuid,
  up_to_seq: z.number().int().nonnegative(),
});

export const zMsgReadClientFrame = frame('msg.read', {
  cid: zUuid,
  up_to_seq: z.number().int().nonnegative(),
});

export const zTypingStartClientFrame = frame('typing.start', { cid: zUuid });
export const zTypingStopClientFrame = frame('typing.stop', { cid: zUuid });

export const zPresenceSubscribeFrame = frame('presence.subscribe', {
  user_ids: z.array(zUuid),
});

export const zPingFrame = frame('ping', {});

/** Discriminated union of all C→S frames. */
export const zClientToServerFrame = z.discriminatedUnion('t', [
  zAuthRefreshFrame,
  zSyncRequestFrame,
  zMsgSendFrame,
  zMsgDeliveredClientFrame,
  zMsgReadClientFrame,
  zTypingStartClientFrame,
  zTypingStopClientFrame,
  zPresenceSubscribeFrame,
  zPingFrame,
]);

/* ---- Server → Client frames (§5.2) ---- */

export const zConnReadyFrame = frame('conn.ready', {
  server_time: zEpochMs,
  resume_token: z.string(),
  user_id: zUuid,
});

export const zAuthOkFrame = frame('auth.ok', { auth_expires_at_ms: zEpochMs });

export const zSyncBatchFrame = frame('sync.batch', {
  cid: zUuid,
  messages: z.array(zWsStoredMessage),
  has_more: z.boolean(),
  next_since_seq: z.number().int().nonnegative(),
});

export const zMsgAckFrame = frame('msg.ack', {
  cid: zUuid,
  client_msg_id: zUlid,
  seq: z.number().int().nonnegative(),
  server_ts: zEpochMs,
});

export const zMsgNewFrame = frame('msg.new', {
  cid: zUuid,
  seq: z.number().int().nonnegative(),
  sender_id: zUuid,
  client_msg_id: zUlid,
  content_type: zContentType,
  ciphertext: zBase64Url,
  ratchet_header: zBase64Url,
  media_id: zUuid.optional(),
  reply_to_seq: z.number().int().nonnegative().optional(),
  server_ts: zEpochMs,
});

export const zMsgDeliveredServerFrame = frame('msg.delivered', {
  cid: zUuid,
  up_to_seq: z.number().int().nonnegative(),
  user_id: zUuid,
});

export const zMsgReadServerFrame = frame('msg.read', {
  cid: zUuid,
  up_to_seq: z.number().int().nonnegative(),
  user_id: zUuid,
});

export const zTypingStartServerFrame = frame('typing.start', {
  cid: zUuid,
  user_id: zUuid,
});

export const zTypingStopServerFrame = frame('typing.stop', {
  cid: zUuid,
  user_id: zUuid,
});

export const zPresenceUpdateFrame = frame('presence.update', {
  user_id: zUuid,
  state: zPresenceState,
  last_seen_at_ms: zEpochMs.nullable().optional(),
});

export const zConvUpdatedFrame = frame('conv.updated', {
  cid: zUuid,
  title: z.string().optional(),
  avatar_url: z.string().url().nullable().optional(),
  by_user_id: zUuid,
});

export const zConvMemberChangedFrame = frame('conv.member_changed', {
  cid: zUuid,
  action: zMemberChangeAction,
  user_id: zUuid,
  role: zMemberRole.optional(),
  by_user_id: zUuid,
});

export const zErrorFrame = frame('error', {
  ref_id: zUlid,
  code: zWsErrorCode,
  message: z.string(),
  retryable: z.boolean(),
});

export const zPongFrame = frame('pong', {});

/** Discriminated union of all S→C frames. */
export const zServerToClientFrame = z.discriminatedUnion('t', [
  zConnReadyFrame,
  zAuthOkFrame,
  zSyncBatchFrame,
  zMsgAckFrame,
  zMsgNewFrame,
  zMsgDeliveredServerFrame,
  zMsgReadServerFrame,
  zTypingStartServerFrame,
  zTypingStopServerFrame,
  zPresenceUpdateFrame,
  zConvUpdatedFrame,
  zConvMemberChangedFrame,
  zErrorFrame,
  zPongFrame,
]);

/**
 * Union of every frame in either direction. Implemented as a plain `z.union`
 * (not `discriminatedUnion`) because some `t` values map to two shapes
 * (server/client variants of `msg.delivered`, `msg.read`, `typing.*`).
 */
export const zWsFrame = z.union([zClientToServerFrame, zServerToClientFrame]);

/* -------------------------------------------------------------------------- */
/*  Parse helpers                                                              */
/* -------------------------------------------------------------------------- */

/** Result of a non-throwing parse — either typed data or a flattened error. */
export type ParseResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: z.ZodError<T> };

/** Non-throwing parse → discriminated result (no exceptions on bad input). */
export function safeParse<T>(schema: z.ZodType<T>, value: unknown): ParseResult<T> {
  const r = schema.safeParse(value);
  return r.success ? { ok: true, data: r.data } : { ok: false, error: r.error };
}

/** Throwing parse — use when invalid input is a programmer error, not user input. */
export function parse<T>(schema: z.ZodType<T>, value: unknown): T {
  return schema.parse(value);
}

/**
 * Parse a raw WS text frame: `JSON.parse` then validate against the full union.
 * Returns a `ParseResult` so the router can emit a `bad_frame` error frame on
 * failure instead of throwing.
 */
export function parseWsFrame(raw: string): ParseResult<z.infer<typeof zWsFrame>> {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return {
      ok: false,
      error: new z.ZodError([
        { code: z.ZodIssueCode.custom, path: [], message: 'frame is not valid JSON' },
      ]),
    };
  }
  return safeParse(zWsFrame, json);
}

/** Map field-level zod issues to the REST error envelope `details` shape (§8.1). */
export function zodToFieldErrors(error: z.ZodError): Array<{ field: string; message: string }> {
  return error.issues.map((issue) => ({
    field: issue.path.join('.') || '(root)',
    message: issue.message,
  }));
}
